import React, { useContext, useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { get } from 'lodash';
import { FormContext } from 'contexts';
import { v4 as uuid } from 'uuid';
import { Button } from 'components/common';
import {
  AuCallout,
  AuFormItemWrapper,
  AuErrorText
} from 'components/DTA';
import { fileData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';

interface FileUploadProps {
  documentType: string | undefined;
  identityType: string | undefined;
}

const FileUpload: React.FC<FileUploadProps> = ({ documentType, identityType }) => {
  const [selectedFiles, setSelectedFiles] = useState<Array<fileData>>([]);
  const { errors, getFieldMeta, setFieldValue } = useFormikContext();
  const { setFormAlert } = useContext(FormContext);
  const fieldError = get(errors, `${identityType}.hasUploadedFiles`);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFieldValue(`${identityType}.isManualUpload`, true);
    const fieldValue = getFieldMeta(`${identityType}.identityFiles`).value as fileData[];

    if (fieldValue) {
      setSelectedFiles(fieldValue);
    }
  }, []);

  useEffect(() => {
    setFieldValue(`${identityType}.identityFiles`, selectedFiles);
    const hasUploadedFile = selectedFiles && selectedFiles.length > 0;
    setFieldValue(`${identityType}.hasUploadedFiles`, hasUploadedFile);
  }, [selectedFiles]);

  const handleChooseFile = ({ currentTarget: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    if (files && files.length) {
      const fileSizeInMB = files[0].size / 1024 / 1024;
      //check for file size
      if (fileSizeInMB > 20) {
        const alert: IFormAlertType = {
          as: 'error',
          title: 'There is a problem uploading your file',
          message: 'The document exceeds the maximum file size of 20MB'
        };
        setFormAlert(alert);
        return;
      }
      //check file extrnsion
      const extension = files[0].name.split('.').pop();
      const fileExtension = extension?.toLowerCase();
      const allowedFileExtensions = ['jpg', 'jpeg', 'bmp', 'png', 'pdf'];
      const isAllowed = allowedFileExtensions.find(ext => (fileExtension === ext));
      if (!isAllowed) {
        const alert: IFormAlertType = {
          as: 'error',
          title: 'There is a problem uploading your file',
          message: <p>Uploaded file named (<strong>{files[0].name}</strong>) is not one of the supported formats. This file has been automatically removed. Please check the files you&lsquo;re trying to upload.</p>
        };
        setFormAlert(alert);
        return;
      }
      //check for already selected file
      const isAlreadySelected = selectedFiles.find(item => item.file.name === files[0].name);
      if (isAlreadySelected) {
        const alert: IFormAlertType = {
          as: 'error',
          title: 'There is a problem uploading your files',
          message: <>We can&apos;t accept files with the same name. Check the names of the files you are trying to upload.</>
        };
        setFormAlert(alert);
        return;
      }
      const id = uuid().toString();
      const url = window.URL.createObjectURL(files[0]);
      const filenames = getFieldMeta(`${identityType}.filename`).value as string[];
      filenames.push(files[0].name);
      setFieldValue(`${identityType}.filename`, filenames);
      const file = { file: files[0], id, blodUrl: url };
      setSelectedFiles(existing => existing.concat(file));
      setFormAlert(null);
    }
  };

  const removeDocument = (id: uuid) => {
    const files = selectedFiles.filter(item => item.id !== id);
    setSelectedFiles(files);
    const filenames = files.map(file => file.file.name);
    setFieldValue(`${identityType}.filename`, filenames);
  };

  return (
    <>
      <AuFormItemWrapper>
        <AuCallout title='How it works'>
          <p>You will need to upload a <a href='https://www.casa.gov.au/standard-page/individual-aviation-reference-number/people-who-can-certify-proof-identity-documents' target="_blank" rel="noopener noreferrer">certified copy</a> of your identification and we will verify your identification manually.</p>
          <p>Your aviation reference number will be issued within five business days of your application being submitted.</p>
        </AuCallout>
      </AuFormItemWrapper>
      <AuFormItemWrapper
        className={
          'au-form-group ' +
          `${getFieldMeta(`${identityType}.hasUploadedFiles`).error ? '  au-form-group--invalid' : ''}`
        }>
        <Field
          name={`${identityType}.hasUploadedFiles`}
          className='au-control-input__input'
        >
          {() => (
            <div className={
              'au-control-input ' +
              `${getFieldMeta(`${identityType}.hasUploadedFiles`).error ? ' au-control-input--invalid' : ''}`
            }>
              <label>Upload a certified copy of your {documentType}</label>
              <br />
              <input
                className='fileInput'
                type="file"
                name="file"
                accept='.pdf,.jpg,.jpeg,.bmp,.png'
                onChange={handleChooseFile}
                ref={fileInputRef}
              />
              <Button as='primary' onClick={
                () => {
                  fileInputRef.current?.click();
                }
              }>Choose file</Button>
              {
                fieldError &&
                <AuErrorText text={get(errors, `${identityType}.hasUploadedFiles`)} />
              }
              <p>Uploaded attachments must be no more than 20mb in size.</p>
              <p>Accepted file types are PDF documents (.pdf) and the following image formats: .jpg, .jpeg, .bmp and .png</p>
            </div>
          )
          }</Field>
        <br />
        <div className='file-picker'>
          <div className="uploaded-files" id="uploaded-files">
            <ul>
              {
                selectedFiles.map(item =>
                  <li key={item.id}>
                    <a
                      target="_blank"
                      href={item.blodUrl}
                      rel="noopener noreferrer"
                      className="file-name"
                      aria-label={item.file.name}
                    >
                      {item.file.name}
                    </a>
                    <span
                      className="remove-file close"
                      role="button"
                      aria-label="Press Enter to delete the file {item.name}"
                      onClick={
                        () => {
                          removeDocument(item.id);
                        }
                      }>
                      x
                    </span>
                  </li>
                )
              }
            </ul>
          </div>
        </div>
      </AuFormItemWrapper>
    </>
  );
};

export default FileUpload;